import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Mountain, Snowflake, Wind, Thermometer, ArrowLeft, Loader2,
  ChevronUp, ChevronDown, Eye, AlertTriangle, Timer, Map,
  TrendingUp, Calendar
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_LOCATION = { lat: 35.2098048, lon: -86.0794285 };

function RatingBadge({ label }) {
  const colors = {
    "Epic": "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    "Excellent": "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    "Very Good": "bg-green-500/20 text-green-400 border-green-500/30",
    "Good": "bg-lime-500/20 text-lime-400 border-lime-500/30",
    "Fair": "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    "Poor": "bg-red-500/20 text-red-400 border-red-500/30",
  };
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${colors[label] || colors["Fair"]}`}>
      {label}
    </span>
  );
}

function SnowBar({ value, max, label, isDarkMode }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className={isDarkMode ? 'text-white/50' : 'text-gray-500'}>{label}</span>
        <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{value}"</span>
      </div>
      <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-white/10' : 'bg-gray-200'}`}>
        <div className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function SkiSnowReportPage() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [searchParams] = useSearchParams();
  const defaultLocation = user?.preferences?.default_location || FALLBACK_LOCATION;
  const lat = parseFloat(searchParams.get("lat") || defaultLocation.lat || FALLBACK_LOCATION.lat);
  const lon = parseFloat(searchParams.get("lon") || defaultLocation.lon || FALLBACK_LOCATION.lon);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(null);

  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const res = await axios.get(`${API}/subscription/status`);
        setIsPremium(res.data?.is_premium || res.data?.is_trial || user?.role === "admin");
      } catch {
        setIsPremium(user?.role === "admin");
      }
    };
    if (user) checkSubscription();
  }, [user]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/weather/ski/detailed`, { params: { lat, lon } });
      setData(resp.data);
    } catch (err) {
      console.error("Ski data fetch error:", err);
    } finally { setLoading(false); }
  }, [lat, lon]);

  useEffect(() => {
    fetchData();
  }, [lat, lon, fetchData]);

  if (isPremium === null) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[#0a0e1a]' : 'bg-gray-50'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  if (!isPremium) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0e1a] text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className="max-w-4xl mx-auto p-6">
          <Link to="/" className={`flex items-center gap-2 mb-8 ${isDarkMode ? 'text-white/60 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}>
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
          <div className="text-center py-20">
            <Mountain className="w-16 h-16 mx-auto mb-4 text-cyan-400 opacity-50" />
            <h2 className="text-2xl font-bold mb-2">Premium Feature</h2>
            <p className={isDarkMode ? 'text-white/50' : 'text-gray-500'}>Ski & Snow Report is available for premium subscribers.</p>
            <Link to="/subscription" className="inline-block mt-4 px-6 py-2 bg-cyan-600 text-white rounded-xl hover:bg-cyan-500 transition-colors">Upgrade to Premium</Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[#0a0e1a]' : 'bg-gray-50'}`}>
        <Loader2 className={`w-8 h-8 animate-spin ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
      </div>
    );
  }

  if (!data?.success) return null;

  const { snow, weather, operations: ops, terrain, conditions_rating: cr, elevation: elev, avalanche_danger: av, is_ski_season, snow_forecast: forecast, resort } = data;
  const maxSnow = Math.max(snow.base_depth_inches, snow.new_7d_inches, 1);

  const getAvColor = (d) => {
    if (d === "Low") return "text-green-400";
    if (d === "Moderate") return "text-yellow-400";
    if (d === "Considerable") return "text-orange-400";
    return "text-red-400";
  };

  return (
    <div data-testid="ski-snow-page" className={`min-h-screen pb-12 ${isDarkMode ? 'bg-[#0a0e1a] text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Link to="/" data-testid="ski-back-btn" className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-white/60 hover:text-white' : 'text-gray-500 hover:text-gray-900'} transition-colors`}>
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <button onClick={fetchData} className={`text-xs px-3 py-1.5 rounded-lg ${isDarkMode ? 'bg-white/10 hover:bg-white/15 text-white/70' : 'bg-gray-200 hover:bg-gray-300 text-gray-600'} transition-colors`}>Refresh</button>
        </div>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2.5 rounded-xl ${isDarkMode ? 'bg-cyan-500/20' : 'bg-cyan-100'}`}>
              <Mountain className={`w-6 h-6 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Ski & Snow Report</h1>
              <p className={`text-sm ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>{resort}</p>
            </div>
          </div>
          {/* Status + Conditions */}
          <div className="flex items-center gap-3 mt-3">
            <span className={`text-xs font-semibold px-3 py-1 rounded-full ${ops.status === 'Open' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
              {ops.status}
            </span>
            <RatingBadge label={cr.label} />
            {!is_ski_season && <span className="text-xs text-yellow-400">Off Season</span>}
          </div>
        </div>

        {/* Snow Conditions */}
        <section className="mb-8">
          <h2 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>Snow Conditions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className={`p-5 rounded-2xl ${isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white border border-gray-200'}`}>
              <div className="flex items-center gap-2 mb-4"><Snowflake className="w-5 h-5 text-cyan-400" /><h3 className="font-semibold">Snow Depth & Accumulation</h3></div>
              <div className="space-y-3">
                <SnowBar value={snow.base_depth_inches} max={Math.max(maxSnow, 80)} label="Base Depth" isDarkMode={isDarkMode} />
                <SnowBar value={snow.new_24h_inches} max={Math.max(snow.new_7d_inches, 12)} label="New (24h)" isDarkMode={isDarkMode} />
                <SnowBar value={snow.new_48h_inches} max={Math.max(snow.new_7d_inches, 12)} label="New (48h)" isDarkMode={isDarkMode} />
                <SnowBar value={snow.new_7d_inches} max={Math.max(snow.new_7d_inches, 24)} label="New (7 day)" isDarkMode={isDarkMode} />
              </div>
              <div className={`mt-3 p-2 rounded-lg text-center ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                <p className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>Surface: <span className="font-semibold">{snow.snow_type}</span></p>
              </div>
            </div>
            <div className={`p-5 rounded-2xl ${isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white border border-gray-200'}`}>
              <div className="flex items-center gap-2 mb-4"><Mountain className="w-5 h-5 text-slate-400" /><h3 className="font-semibold">Mountain Info</h3></div>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className={isDarkMode ? 'text-white/50' : 'text-gray-500'}>Summit</span>
                  <span className="font-medium">{elev.summit.toLocaleString()} ft</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className={isDarkMode ? 'text-white/50' : 'text-gray-500'}>Base</span>
                  <span className="font-medium">{elev.base.toLocaleString()} ft</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className={isDarkMode ? 'text-white/50' : 'text-gray-500'}>Vertical Drop</span>
                  <span className="font-medium">{elev.vertical_drop.toLocaleString()} ft</span>
                </div>
                <div className={`h-px ${isDarkMode ? 'bg-white/10' : 'bg-gray-200'}`} />
                <div className="flex justify-between text-sm">
                  <span className={isDarkMode ? 'text-white/50' : 'text-gray-500'}>Temperature</span>
                  <span className="font-medium">{weather.temperature_f}°F / {weather.temperature_c}°C</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className={isDarkMode ? 'text-white/50' : 'text-gray-500'}>Wind</span>
                  <span className="font-medium">{weather.wind_mph} mph</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className={isDarkMode ? 'text-white/50' : 'text-gray-500'}>Visibility</span>
                  <span className="font-medium">{weather.visibility}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className={isDarkMode ? 'text-white/50' : 'text-gray-500'}>Avalanche Danger</span>
                  <span className={`font-semibold ${getAvColor(av)}`}>{av}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Operations */}
        <section className="mb-8">
          <h2 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>Operations</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-gray-50 border border-gray-100'}`}>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{ops.lifts_open}<span className={`text-sm font-normal ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>/{ops.lifts_total}</span></p>
              <p className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>Lifts Open</p>
            </div>
            <div className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-gray-50 border border-gray-100'}`}>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{ops.trails_open}<span className={`text-sm font-normal ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>/{ops.trails_total}</span></p>
              <p className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>Trails Open</p>
            </div>
            <div className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-gray-50 border border-gray-100'}`}>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{ops.terrain_parks_open}</p>
              <p className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>Terrain Parks</p>
            </div>
            <div className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-gray-50 border border-gray-100'}`}>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{terrain.acres.toLocaleString()}</p>
              <p className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>Skiable Acres</p>
            </div>
          </div>
          {/* Terrain Breakdown */}
          <div className={`mt-4 p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-gray-50 border border-gray-100'}`}>
            <p className={`text-xs font-semibold mb-2 ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>Terrain Breakdown</p>
            <div className="flex h-4 rounded-full overflow-hidden gap-0.5">
              <div className="bg-green-400 rounded-l-full" style={{ width: `${terrain.beginner_percent}%` }} title={`Beginner ${terrain.beginner_percent}%`} />
              <div className="bg-blue-400" style={{ width: `${terrain.intermediate_percent}%` }} title={`Intermediate ${terrain.intermediate_percent}%`} />
              <div className="bg-slate-800 rounded-r-full" style={{ width: `${terrain.advanced_percent}%` }} title={`Advanced ${terrain.advanced_percent}%`} />
            </div>
            <div className="flex justify-between text-xs mt-2">
              <span className="text-green-400">Beginner {terrain.beginner_percent}%</span>
              <span className="text-blue-400">Intermediate {terrain.intermediate_percent}%</span>
              <span className={isDarkMode ? 'text-white/60' : 'text-gray-600'}>Advanced {terrain.advanced_percent}%</span>
            </div>
          </div>
        </section>

        {/* 7-Day Snow Forecast */}
        {forecast && forecast.length > 0 && (
          <section className="mb-8">
            <h2 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>7-Day Snow Forecast</h2>
            <div className={`rounded-2xl overflow-hidden ${isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white border border-gray-200'}`}>
              <div className={`grid grid-cols-5 text-xs font-semibold py-3 px-4 ${isDarkMode ? 'bg-white/5 text-white/50' : 'bg-gray-100 text-gray-500'}`}>
                <span>Date</span>
                <span className="text-center">Snowfall</span>
                <span className="text-center">High</span>
                <span className="text-center">Low</span>
                <span className="text-center">Wind</span>
              </div>
              <div className={`divide-y ${isDarkMode ? 'divide-white/5' : 'divide-gray-100'}`}>
                {forecast.map((day, idx) => (
                  <div key={`item-${idx}`} className={`grid grid-cols-5 text-xs py-3 px-4 ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
                    <span className={`font-medium ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`}>
                      {new Date(day.date + "T12:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                    </span>
                    <span className={`text-center font-semibold ${day.snowfall_inches > 0 ? 'text-cyan-400' : (isDarkMode ? 'text-white/30' : 'text-gray-300')}`}>
                      {day.snowfall_inches > 0 ? `${day.snowfall_inches}"` : '-'}
                    </span>
                    <span className={`text-center ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{day.temp_high_f != null ? `${day.temp_high_f}°` : '-'}</span>
                    <span className={`text-center ${isDarkMode ? 'text-white/60' : 'text-gray-500'}`}>{day.temp_low_f != null ? `${day.temp_low_f}°` : '-'}</span>
                    <span className={`text-center ${isDarkMode ? 'text-white/50' : 'text-gray-400'}`}>{day.wind_max_mph != null ? `${day.wind_max_mph}mph` : '-'}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
